import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Plus, X } from "lucide-react";

import { AddressAutocomplete } from "@/components/ui/address-autocomplete";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils/cn";

import {
  locationAddressPatch,
  newLocationRow,
  type LocationErrors,
  type LocationRow,
} from "../../../lib/location";
import { ADDRESS_LIMIT } from "../../../lib/text";
import type { TerritoryRegion } from "../../../types";

/** A location's address, with suggestions; the parent applies the patch. */
function AddressRow({
  label,
  value,
  onChange,
  onPatch,
  territories,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onPatch: (patch: { address: string; region?: string; mrc?: string }) => void;
  territories: TerritoryRegion[];
}) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <AddressAutocomplete
        id={id}
        value={value}
        maxLength={ADDRESS_LIMIT}
        onChange={onChange}
        onResolved={(result, suggestion) =>
          onPatch(locationAddressPatch(result, suggestion.text, territories))
        }
      />
    </div>
  );
}

const selectClass =
  "h-10 w-full min-w-0 rounded-md border border-input bg-background px-3 text-sm";

/**
 * Where the organization is — the SPA side of the Django step's
 * `_organization_location.html`: head office (address, region, MRC), its other
 * places of business (zero or more, each with the same three). The server
 * derives the directory's regions from the head office and the places.
 */
export function LocationSection({
  territories,
  address,
  onAddress,
  region,
  onRegion,
  mrc,
  onMrc,
  places,
  onPlaces,
  errors,
}: {
  territories: TerritoryRegion[];
  address: string;
  onAddress: (v: string) => void;
  region: string;
  onRegion: (v: string) => void;
  mrc: string;
  onMrc: (v: string) => void;
  places: LocationRow[];
  onPlaces: (next: LocationRow[]) => void;
  errors: LocationErrors;
}) {
  const { t } = useTranslation();
  const regionId = useId();
  const mrcId = useId();
  const mrcsOf = (id: string) => territories.find((r) => r.region === id)?.mrcs ?? [];

  const patchPlace = (key: string, patch: Partial<LocationRow>) =>
    onPlaces(places.map((p) => (p.key === key ? { ...p, ...patch } : p)));

  return (
    <section className="space-y-4">
      <h4 className="text-base font-semibold">{t("expert.register.locationHeading")}</h4>
      <AddressRow
        label={t("expert.register.fields.head_office_address")}
        value={address}
        onChange={onAddress}
        onPatch={(patch) => {
          onAddress(patch.address);
          if (patch.region !== undefined && patch.mrc !== undefined) {
            onRegion(patch.region);
            onMrc(patch.mrc);
          }
        }}
        territories={territories}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={regionId} className="block">
            <span className="mr-0.5 text-primary">*</span>
            {t("expert.register.fields.head_office_region")}
          </Label>
          <select
            id={regionId}
            value={region}
            aria-invalid={errors.head_office_region ? true : undefined}
            onChange={(e) => {
              onRegion(e.target.value);
              // An MRC only makes sense within its region.
              if (!mrcsOf(e.target.value).includes(mrc)) onMrc("");
            }}
            className={cn(selectClass, errors.head_office_region && "border-destructive")}
          >
            <option value="">{t("expert.register.chooseRegion")}</option>
            {territories.map((r) => (
              <option key={r.region} value={r.region}>
                {r.name}
              </option>
            ))}
          </select>
          {errors.head_office_region && (
            <p className="text-sm text-destructive">{errors.head_office_region}</p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={mrcId} className="block">
            <span className="mr-0.5 text-primary">*</span>
            {t("expert.register.fields.head_office_mrc")}
          </Label>
          <select
            id={mrcId}
            value={mrc}
            disabled={!region}
            aria-invalid={errors.head_office_mrc ? true : undefined}
            onChange={(e) => onMrc(e.target.value)}
            className={cn(selectClass, errors.head_office_mrc && "border-destructive")}
          >
            <option value="">{t("expert.register.chooseMrc")}</option>
            {mrcsOf(region).map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          {errors.head_office_mrc && (
            <p className="text-sm text-destructive">{errors.head_office_mrc}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">{t("expert.register.otherPlacesHeading")}</p>
        <p className="text-sm text-muted-foreground">{t("expert.register.otherPlacesHelp")}</p>
        {places.map((place, index) => {
          const placeErrors = errors.places?.[index] ?? {};
          const options = mrcsOf(place.region);
          return (
            <div key={place.key} className="space-y-3 rounded-lg border p-3">
              <AddressRow
                label={t("expert.register.fields.place_address")}
                value={place.address}
                onChange={(v) => patchPlace(place.key, { address: v })}
                onPatch={(patch) => patchPlace(place.key, patch)}
                territories={territories}
              />
              <div className="space-y-1.5">
                <Label htmlFor={`${place.key}-region`} className="block">
                  <span className="mr-0.5 text-primary">*</span>
                  {t("expert.register.fields.place_region")}
                </Label>
                <select
                  id={`${place.key}-region`}
                  value={place.region}
                  aria-invalid={placeErrors.region ? true : undefined}
                  onChange={(e) =>
                    // A new region offers other MRCs: clear the chosen one.
                    patchPlace(place.key, { region: e.target.value, mrc: "" })
                  }
                  className={cn(selectClass, placeErrors.region && "border-destructive")}
                >
                  <option value="">{t("expert.register.chooseRegion")}</option>
                  {territories.map((r) => (
                    <option key={r.region} value={r.region}>
                      {r.name}
                    </option>
                  ))}
                </select>
                {placeErrors.region && (
                  <p className="text-sm text-destructive">{placeErrors.region}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`${place.key}-mrc`} className="block">
                  <span className="mr-0.5 text-primary">*</span>
                  {t("expert.register.fields.place_mrc")}
                </Label>
                <select
                  id={`${place.key}-mrc`}
                  value={place.mrc}
                  aria-invalid={placeErrors.mrc ? true : undefined}
                  onChange={(e) => patchPlace(place.key, { mrc: e.target.value })}
                  className={cn(selectClass, placeErrors.mrc && "border-destructive")}
                >
                  <option value="">{t("expert.register.chooseMrc")}</option>
                  {options.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                {placeErrors.mrc && (
                  <p className="text-sm text-destructive">{placeErrors.mrc}</p>
                )}
              </div>
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-destructive"
                  onClick={() => onPlaces(places.filter((p) => p.key !== place.key))}
                >
                  <X className="size-4" aria-hidden />
                  {t("expert.register.removePlace")}
                </Button>
              </div>
            </div>
          );
        })}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onPlaces([...places, newLocationRow()])}
        >
          <Plus className="size-4" aria-hidden />
          {t("expert.register.addPlace")}
        </Button>
      </div>
    </section>
  );
}
